import type * as React from "react"
import { router, usePage } from "@inertiajs/react"
import {
  ChevronDown,
  Eye,
  Globe,
  LayoutDashboard,
  LogOut,
  Pencil,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ADMIN_BG, adminNavSections } from "@/lib/admin-nav"
import { cn } from "@/lib/utils"
import type { AdminBarProps, PageProps } from "@/types/inertia"

// The WordPress-style strip across the top of every page while an admin is
// signed in: on a public page it leads to the admin portal, the portal's
// sections and the screen that edits this page; in the portal it leads to the
// public page being edited and both websites. Log out on the right. Renders
// nothing without the admin_bar prop, so visitors never see it.
//
// Sticky and in normal flow, so page content simply starts below it; the two
// fixed public navs and the admin sidebar read --admin-bar-h (set by the
// layouts via adminBarOffset) to sit under it.

export const ADMIN_BAR_HEIGHT = "2.25rem" // h-9

export function adminBarOffset(present: boolean) {
  return { "--admin-bar-h": present ? ADMIN_BAR_HEIGHT : "0px" } as React.CSSProperties
}

const ITEM =
  "flex h-full shrink-0 cursor-pointer items-center gap-1.5 px-2.5 text-white/80 no-underline transition-colors hover:bg-white/10 hover:text-white focus-visible:bg-white/10 focus-visible:outline-none [&_svg]:h-3.5 [&_svg]:w-3.5 [&_svg]:shrink-0"

// Cross-layout links are plain anchors: a public page and the admin portal
// are different React trees, and a full load is the honest transition.
function BarLink({
  href,
  icon: Icon,
  children,
  className,
}: {
  href: string
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
  className?: string
}) {
  return (
    <a href={href} className={cn(ITEM, className)}>
      <Icon />
      <span className="truncate">{children}</span>
    </a>
  )
}

// The other brand's domain has no session cookie, so the link becomes a form
// POST to the handoff endpoint, which redirects there with a one-shot login
// token (AdminHandoff). Built on click — plain-DOM, never during render.
export function openViaHandoff(brand: string, to: string) {
  const token =
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
  const form = document.createElement("form")
  form.method = "post"
  form.action = "/admin/handoff"
  const fields: Array<[string, string]> = [
    ["authenticity_token", token],
    ["brand", brand],
    ["to", to],
  ]
  for (const [name, value] of fields) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    form.append(input)
  }
  document.body.append(form)
  form.submit()
  form.remove()
}

// Same-host targets link by path: the brand's configured host may carry a
// different port or scheme from the one the browser reached us on.
function ViewPageLink({ view }: { view: NonNullable<AdminBarProps["view"]> }) {
  if (!view.cross_host) {
    return (
      <BarLink href={view.path} icon={Eye}>
        {view.label}
      </BarLink>
    )
  }
  return (
    <button
      type="button"
      className={ITEM}
      onClick={() => openViaHandoff(view.brand_key, view.path)}
    >
      <Eye />
      <span className="truncate">{view.label}</span>
    </button>
  )
}

function MenuTrigger({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}) {
  return (
    <DropdownMenuTrigger asChild>
      <button type="button" className={cn(ITEM, "data-[state=open]:bg-white/10")}>
        <Icon />
        <span className="hidden sm:inline">{children}</span>
        <ChevronDown className="opacity-60" />
      </button>
    </DropdownMenuTrigger>
  )
}

// Public pages: every section of the admin portal, grouped as in the sidebar.
function SectionsMenu() {
  const sections = adminNavSections()
  return (
    <DropdownMenu>
      <MenuTrigger icon={LayoutDashboard}>Admin</MenuTrigger>
      <DropdownMenuContent align="start" className="z-[70] max-h-[calc(100vh-4rem)] overflow-y-auto">
        {sections.map((section, i) => (
          <div key={section.label}>
            {i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel>{section.label}</DropdownMenuLabel>
            {section.items.map((item) => {
              const Icon = item.icon
              return (
                <DropdownMenuItem key={item.href} asChild>
                  <a href={item.href} className="no-underline">
                    <Icon /> {item.label}
                  </a>
                </DropdownMenuItem>
              )
            })}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Admin screens: the two public websites.
function WebsitesMenu({ websites }: { websites: AdminBarProps["websites"] }) {
  return (
    <DropdownMenu>
      <MenuTrigger icon={Globe}>View website</MenuTrigger>
      <DropdownMenuContent align="start" className="z-[70]">
        {websites.map((site) =>
          site.cross_host ? (
            <DropdownMenuItem key={site.key} onSelect={() => openViaHandoff(site.key, "/")}>
              <Globe /> {site.name}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem key={site.key} asChild>
              <a href="/" className="no-underline">
                <Globe /> {site.name}
              </a>
            </DropdownMenuItem>
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function AdminBar({ variant }: { variant: "public" | "admin" }) {
  const { admin_bar } = usePage<PageProps>().props
  if (!admin_bar) return null
  const { email, admin_url, edit, view, websites, logout_return_to } = admin_bar

  function logOut() {
    router.delete("/logout", {
      data: logout_return_to ? { return_to: logout_return_to } : {},
    })
  }

  return (
    <div
      role="navigation"
      aria-label="Admin bar"
      className="sticky top-0 z-[60] h-9 text-xs text-white"
      style={{ backgroundColor: ADMIN_BG }}
    >
      <div className="flex h-full items-center justify-between gap-2 pr-1 sm:pr-2">
        <div className="flex h-full min-w-0 items-center">
          {variant === "public" ? (
            <>
              <SectionsMenu />
              {edit && (
                <BarLink href={edit.url} icon={Pencil} className="min-w-0">
                  {edit.label}
                </BarLink>
              )}
            </>
          ) : (
            <>
              <BarLink href={admin_url} icon={LayoutDashboard}>
                Admin
              </BarLink>
              <WebsitesMenu websites={websites} />
              {view && <ViewPageLink view={view} />}
            </>
          )}
        </div>
        <div className="flex h-full shrink-0 items-center">
          <span className="hidden max-w-[14rem] truncate px-2 text-white/50 sm:inline">{email}</span>
          <button type="button" onClick={logOut} className={ITEM}>
            <LogOut />
            <span>Log out</span>
          </button>
        </div>
      </div>
    </div>
  )
}
